/**
 * Indicador del outbox offline (ADR-0007).
 * Muestra cuántas operaciones están esperando red y permite forzar el envío.
 */
import { useCallback, useEffect, useState } from 'react';
import { drainOutbox, listOutbox, pendingCount } from '../lib/outbox.js';
import type { OutboxEntry } from '../lib/db.js';
import { useT } from '../lib/i18n.js';
import { Badge, Dialog, Button } from './ui.js';

export function OutboxBadge(): JSX.Element | null {
  const t = useT();
  const [pending, setPending] = useState(0);
  const [entries, setEntries] = useState<OutboxEntry[]>([]);
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);

  const refresh = useCallback(async () => {
    setPending(await pendingCount());
    setEntries(await listOutbox());
  }, []);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), 5_000);
    const onOnline = (): void => {
      setOnline(true);
      void drainOutbox().then(refresh);
    };
    const onOffline = (): void => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [refresh]);

  if (pending === 0 && online) return null;

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="pit-touch">
        <Badge tone={online ? 'warning' : 'danger'} pulse={!online}>
          {online ? t('app.online') : t('app.offline')}
          {pending > 0 ? ` · ${pending}` : ''}
        </Badge>
      </button>

      <Dialog
        open={open}
        title={t('app.offline')}
        closeLabel={t('app.close')}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('app.close')}
            </Button>
            <Button
              onClick={() => {
                void drainOutbox().then(refresh);
              }}
            >
              {t('app.retry')}
            </Button>
          </>
        }
      >
        {entries.length === 0 ? (
          <p>{t('cashier.noPending')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {entries.map((entry) => (
              <li key={entry.id} className="pit-card flex items-center justify-between gap-2 px-3 py-2">
                <span className="pit-mono text-[var(--pit-text-xs)]">{entry.label}</span>
                <Badge tone={entry.status === 'failed' ? 'danger' : 'warning'}>{entry.status}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </>
  );
}
